import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { useVerificationStatus } from "../hooks/useVerificationStatus";
import { isNewContractRequest, isVerificationCompleteForRoute } from "../lib/verification";

type Props = { children: ReactNode };

export default function ProtectedRoute({ children }: Props) {
  const { user, ready } = useAuth();
  const location = useLocation();
  const path = location.pathname;
  // Verification pages must remain reachable while the required checks are incomplete.
  const skip = ["/onboarding", "/ekyc/reverify", "/account/verify", "/mypage"].some(prefix => path === prefix || path.startsWith(`${prefix}/`));
  const next = location.pathname + location.search + location.hash;
  const { account, loading, error, retry } = useVerificationStatus({ enabled: !skip, watchSearch: !isNewContractRequest(next) });

  if (!ready) return <p role="status" className="p-6 text-center text-sm text-stone-500">로그인 상태를 확인하고 있어요.</p>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace state={{ next, from: location }} />;
  if (skip) return <>{children}</>;
  if (loading) return <p role="status" className="p-6 text-center text-sm text-stone-500">인증 상태를 확인하고 있어요.</p>;
  if (error || !account) return <div className="mx-auto max-w-md px-5 py-12 text-center"><p role="alert" className="text-sm text-stone-600">인증 상태를 확인하지 못했어요. 잠시 후 다시 시도해 주세요.</p><button type="button" onClick={retry} className="mt-5 min-h-11 rounded-xl bg-orange-600 px-6 py-3 text-sm font-semibold text-white">다시 확인</button></div>;
  if (!isVerificationCompleteForRoute(account, next)) return <Navigate to={`/onboarding?next=${encodeURIComponent(next)}`} replace state={{ next }} />;
  return <>{children}</>;
}
